/**
 * `list-base-deviation-reports`, `run-base-deviation-report` and
 * `get-base-deviation-report` — CLM-112 (FR-38, NFR-18, DR-11's test). The
 * refinements R-112-1 … R-112-17 are proposals made while designing
 * (`phase4-specs/CLM-112-base-msa-deviation-report.md` §3); D-2.1-8, D-2.1-11
 * and D-2.1-12, which they lean on, are recommendations, not ratified decisions.
 *
 * A reviewer compares an incoming counterparty MSA (the **subject**) with the
 * nominated base MSA (the **base**, CLM-111) and sees, per clause, whether it is
 * Standard, Modified, missing or extra — and, as NFR-18 requires, an explicit
 * list of what could not be compared. It is a **join through the clause
 * library**, not a comparison engine: both contracts are already segmented into
 * `clm.contract_clause` and matched by CLM-34's matcher, and the report reads
 * those rows. No model is called and no vector is read (R-112-4).
 *
 * ## Seven line categories, four words (R-112-2)
 *
 * {@link BASE_DEVIATION_CATEGORIES} maps onto the ticket's vocabulary through
 * {@link BASE_DEVIATION_CATEGORY_WORD}: `unchanged` is Standard; `modified`,
 * `different_values` and `different_library_version` are Modified; `missing`
 * is missing; `added_library_clause` and `added_bespoke` are extra. The join is
 * on `clause_code`, not `clause_id` (R-112-1): `clause_id` names one *version*
 * of a clause, so a base matched to v1 and a subject matched to v2 would
 * otherwise read as missing plus extra, which is false.
 *
 * ## Honesty (NFR-18, R-112-4, R-112-14)
 *
 * Every paragraph that took part in no line is listed in `not_compared` with a
 * reason from {@link NOT_COMPARED_REASONS}, and every stretch of text no
 * paragraph covers is listed too (`not_segmented_text`). A `missing` or
 * `added_*` line carries `confirmed`, true only when the other side has nothing
 * unresolved that could be the counterpart. No line may rest on cosine alone.
 *
 * ## A persisted run, re-opened as computed (R-112-8, DR-11)
 *
 * A run is stored as an immutable snapshot (`clm.base_deviation_report` +
 * `_result`, 0102). Re-opening it shows the result exactly as computed, beside
 * a live `now` block that says whether the base version is still current and
 * whether the facts it rests on have changed. **Run again** always creates a
 * new report; nothing is ever updated.
 *
 * ## Gates (R-112-13) and scope (R-112-12)
 *
 * All three are `clause:review_match` — Admin and Legal (D-2.1-11). The GET of
 * one report writes the gateway's read audit event, as `retrieve-contract` and
 * `clause-matches` do; the list is counts and writes none. Every statement that
 * reads or writes **both** contracts' data is scoped on both, and the three
 * reads of one contract (the subject, the nomination's base, the current base)
 * are each scoped on that contract before anything about the pair is read: a
 * caller who cannot see the base cannot run or read a report against it
 * (DR-11 snag (2) is not met by this build — Q-112-1).
 *
 * ## No text (rule 13)
 *
 * The wire and the snapshot carry spans, ids, states, clause codes, versions,
 * value **spans** and quoted `deviation_note`s — never a slice of `full_text`.
 * The page renders slices itself from `retrieve-contract include: ['full_text']`.
 */
import { z } from 'zod';
import { ClauseMatchStateSchema, ConfidenceBandSchema } from '../vocabulary.js';
import { ConversionCaveatSchema } from '../provenance.js';
import { ClauseCodeSchema, ContractKeySchema, IsoTimestampUtcSchema, OpaqueIdSchema, Sha256Schema, TextSpanSchema, UpnSchema, VersionNoSchema, bigintIdTextSchema, } from '../primitives.js';
import { BASE_CONTRACT_TYPES, BaseContractNominationSchema, BaseContractTypeSchema, NominationIdSchema, } from './base-contracts.js';
/* -------------------------------------------------------------------------- */
/* Ids and versions                                                            */
/* -------------------------------------------------------------------------- */
/**
 * `clm.base_deviation_report.report_id` — a `bigint` identity, carried as a
 * decimal string for `NominationIdSchema`'s reason: `pg` returns `int8` as a
 * string, and a JavaScript number loses precision above 2^53. It is the GET's
 * path parameter, so it is bounded by the `int8` range too: an id past it is a
 * `400` before any statement, never a 22003 at `r.report_id = $1::bigint`.
 */
export const BaseDeviationReportIdSchema = bigintIdTextSchema('expected a report id');
/**
 * `clm.contract_clause.contract_clause_id` as a decimal string — also a `bigint`
 * identity (0080). Response-only today; bounded the same way so the one shape
 * is safe if it ever becomes an input.
 */
export const ContractClauseIdSchema = bigintIdTextSchema('expected a contract_clause_id');
/**
 * The snapshot's shape version, stored on the run row (`ck_base_deviation_report_result_schema_version`,
 * 0102) and inside the snapshot. Bump it with any change to {@link BaseDeviationResultSchema}: a stored
 * `bdr-1` result must stay readable by the schema that wrote it.
 */
export const BASE_DEVIATION_RESULT_SCHEMA_VERSION = 'bdr-1';
/* -------------------------------------------------------------------------- */
/* Vocabularies                                                                */
/* -------------------------------------------------------------------------- */
/**
 * The seven line categories (R-112-2). A **paired** line — the clause on both
 * sides — takes the first of `modified`, `different_values`,
 * `different_library_version`, `unchanged` that applies; that array order is
 * {@link BASE_DEVIATION_PAIRED_PRECEDENCE}.
 */
export const BASE_DEVIATION_CATEGORIES = [
    /** Same `clause_code`, both Standard, same version(s), equal value tuples. */
    'unchanged',
    /** Either side is Modified against the library — its `deviation_note` is quoted. */
    'modified',
    /** Both Standard, and the ordered value tuples differ — FR-38's altered liability cap. */
    'different_values',
    /** Both Standard, equal values, matched to different versions of the clause (R-112-1). */
    'different_library_version',
    /** The base has the clause and the subject does not — FR-38's anti-join. */
    'missing',
    /** The subject is matched to a library clause the base lacks. */
    'added_library_clause',
    /** A settled bespoke subject paragraph — "a clause they added". */
    'added_bespoke',
];
export const BaseDeviationCategorySchema = z.enum(BASE_DEVIATION_CATEGORIES);
/** R-112-2's order: a paired line takes the first category that applies. */
export const BASE_DEVIATION_PAIRED_PRECEDENCE = [
    'modified',
    'different_values',
    'different_library_version',
    'unchanged',
];
/** The one-sided categories — the only ones that carry `confirmed` (R-112-4). */
export const BASE_DEVIATION_ONE_SIDED_CATEGORIES = [
    'missing',
    'added_library_clause',
    'added_bespoke',
];
/** The ticket's four words, as the page groups the categories. */
export const BASE_DEVIATION_CATEGORY_WORD = Object.freeze({
    unchanged: 'Standard',
    modified: 'Modified',
    different_values: 'Modified',
    different_library_version: 'Modified',
    missing: 'missing',
    added_library_clause: 'extra',
    added_bespoke: 'extra',
});
/**
 * How one paragraph's outcome reads off its `contract_clause` row
 * (`paragraphResolutionOf` in `@clm/clause-mining`, R-112-3, spec §3.2).
 * `library_match` and `bespoke` are **resolved**; every other value is a
 * reason the paragraph could not be compared.
 */
export const PARAGRAPH_RESOLUTIONS = [
    'library_match',
    'bespoke',
    /** R-112-17 — the stored span no longer describes the contract's current text. */
    'span_drift',
    /** `Rejected`: the shortlist was wrong, not the paragraph. Never a match. */
    'shortlist_rejected',
    /** The matcher has not banded this row (an unembedded paragraph after a run). */
    'not_matched_yet',
    /** MEDIUM with no clause — refused, unadjudicated, residue-held, or a model MEDIUM bespoke. */
    'awaiting_review',
    /** §4C — a restricted contract's unsettled paragraph; no model may see it. */
    'withheld_restricted',
    /** A vector exists but no library vector shares its model — says nothing about the paragraph. */
    'no_library_vectors',
    /** `below_low`: decided by cosine alone, so never a difference line (NFR-18). */
    'vector_distance_only',
];
export const ParagraphResolutionSchema = z.enum(PARAGRAPH_RESOLUTIONS);
/** The resolutions that are not compared — every value but the two resolved ones. */
export const UNRESOLVED_PARAGRAPH_RESOLUTIONS = PARAGRAPH_RESOLUTIONS.filter((value) => value !== 'library_match' && value !== 'bespoke');
/**
 * What a paragraph's resolution rests on (spec §3.2's evidence column).
 * `cosine` is recorded so NFR-18's property can be asserted: no line's only
 * evidence is `cosine`.
 */
export const PARAGRAPH_EVIDENCE = ['human', 'text', 'model', 'cosine'];
export const ParagraphEvidenceSchema = z.enum(PARAGRAPH_EVIDENCE);
/**
 * Why something was not compared (NFR-18). The unresolved resolutions, plus
 * `bespoke_unpaired` — a settled bespoke **base** paragraph that no subject
 * paragraph equals by fingerprint (R-112-7), which a library-mediated join
 * cannot see — and `not_segmented_text`, text no paragraph row covers
 * (R-112-14: a paragraph held for FR-29 residue at segmentation has no row).
 */
export const NOT_COMPARED_REASONS = [...UNRESOLVED_PARAGRAPH_RESOLUTIONS, 'bespoke_unpaired', 'not_segmented_text'];
export const NotComparedReasonSchema = z.enum(NOT_COMPARED_REASONS);
/** The not-compared reasons that name a paragraph row (every reason but `not_segmented_text`). */
export const PARAGRAPH_NOT_COMPARED_REASONS = [...UNRESOLVED_PARAGRAPH_RESOLUTIONS, 'bespoke_unpaired'];
/**
 * Why a contract cannot be a report's subject (R-112-10). Each is a
 * `field_errors` code on a `400`; checked in this order.
 */
export const BASE_DEVIATION_SUBJECT_REFUSALS = [
    /** The subject's type is not the nomination's (or no base type is enabled for it). */
    'wrong_contract_type',
    /** It amends another contract and holds only changes — run the report on the agreement it amends. */
    'amendment',
    /** It is the base itself. */
    'same_as_base',
    /** R-111-9 — the base's contract has since been re-typed away from the nomination's type. */
    'base_type_mismatch',
];
export const BaseDeviationSubjectRefusalSchema = z.enum(BASE_DEVIATION_SUBJECT_REFUSALS);
/**
 * One side's readiness (R-112-10). The definitions are the spec's:
 * `not_converted` is `full_text IS NULL`; `not_segmented` is no
 * `contract_clause` row; `not_matched` is **every** row's `confidence_band`
 * NULL (a row still NULL after a run does not block — it is listed
 * `not_matched_yet`); `spans_stale` is every row failing R-112-17's span
 * check.
 */
export const BASE_DEVIATION_SIDE_READINESS = ['ready', 'not_converted', 'not_segmented', 'not_matched', 'spans_stale'];
export const BaseDeviationSideReadinessSchema = z.enum(BASE_DEVIATION_SIDE_READINESS);
/**
 * The `409` codes a run can meet for readiness — each side's not-ready state,
 * the base's with a `base_` prefix. Never an empty report (R-112-10).
 */
export const BASE_DEVIATION_READINESS = [
    'not_converted',
    'not_segmented',
    'not_matched',
    'spans_stale',
    'base_not_converted',
    'base_not_segmented',
    'base_not_matched',
    'base_spans_stale',
];
/**
 * A run's `409` when the write's own predicate admits nothing after every read
 * passed: a contract was re-converted, re-typed or re-linked between the reads
 * and the insert (CLM-111's `contract_changed` precedent).
 */
export const BASE_DEVIATION_CONTRACT_CHANGED = 'contract_changed';
/** The `409` codes of a run, in the order the page's copy is keyed. */
export const BASE_DEVIATION_RUN_CONFLICTS = [...BASE_DEVIATION_READINESS, BASE_DEVIATION_CONTRACT_CHANGED];
/**
 * The `409` code for one side's not-ready state — `side_readiness` as is for
 * the subject, with a `base_` prefix for the base. `null` when the side is ready.
 */
export function baseDeviationReadinessCode(side, readiness) {
    if (readiness === 'ready')
        return null;
    return side === 'subject' ? readiness : `base_${readiness}`;
}
/**
 * Where a quoted `deviation_note` came from (R-112-15, CR-01). `deterministic`
 * is stage ②'s fixed note on a restricted contract, which the matcher writes
 * with no model and records in the contract's one `deterministic_matches` row.
 */
export const BASE_DEVIATION_NOTE_SOURCES = ['human', 'deterministic', 'model'];
/**
 * The first reason `subject` cannot be compared with `nomination` as a `type`
 * base, or `null` when it can (R-112-10). Pure: the API refuses on it and the
 * contract record decides whether to offer the section with it, so the two
 * cannot disagree — the `baseContractIneligibility` pattern. `nomination` is
 * `null` when no base is visible; the checks that need one are then skipped.
 *
 * **Any lifecycle is admitted**, Draft included — incoming counterparty paper
 * is usually unsigned. **Restricted is admitted** — the report calls no model.
 * The API's `INSERT … SELECT` restates the same predicate, so the database is
 * what makes an ineligible report impossible.
 */
export function baseDeviationSubjectRefusal(type, subject, nomination) {
    if (subject.contract_type !== type)
        return 'wrong_contract_type';
    if (nomination !== null && nomination.contract_type !== type)
        return 'wrong_contract_type';
    if (subject.amends_contract_key !== null)
        return 'amendment';
    if (nomination !== null && subject.contract_key === nomination.contract_key)
        return 'same_as_base';
    if (nomination !== null && nomination.base_contract_type !== nomination.contract_type)
        return 'base_type_mismatch';
    return null;
}
/** Whether `contractType` has an enabled base type — the section is offered for these only. */
export function isBaseDeviationType(contractType) {
    return BASE_CONTRACT_TYPES.includes(contractType);
}
/* -------------------------------------------------------------------------- */
/* The snapshot                                                                */
/* -------------------------------------------------------------------------- */
/** Array ceilings. Generous at rule-17 volume (an MSA is tens to a few hundred paragraphs), finite by design. */
const MAX_LINES = 2_000;
const MAX_PARAGRAPHS_PER_LINE = 500;
const MAX_NOT_COMPARED = 5_000;
const MAX_VALUE_SPANS = 2_000;
const Count = z.number().int().min(0);
/** A span into the contract's `full_text`, half-open, never empty. */
const SpanSchema = TextSpanSchema.strict().refine((span) => span.end_offset > span.start_offset, {
    message: 'a span is non-empty and half-open',
});
/** One paragraph as the report names it — a span and the row's states, never its text. */
export const BaseDeviationParagraphSchema = z
    .object({
    contract_clause_id: ContractClauseIdSchema,
    paragraph_ref: z.string().min(1).max(60).nullable(),
    span: SpanSchema,
    match_state: ClauseMatchStateSchema.nullable(),
    confidence_band: ConfidenceBandSchema.nullable(),
    resolution: ParagraphResolutionSchema,
    /** `null` exactly when nothing settled the paragraph (an unresolved row with no evidence to cite). */
    evidence: ParagraphEvidenceSchema.nullable(),
    /** R-112-6 — an unreviewed MEDIUM library match: a person should decide. */
    provisional: z.boolean(),
    reviewed: z.boolean(),
})
    .strict();
/** A quoted `deviation_note` and where it came from (R-112-15). Always "against the library clause". */
export const BaseDeviationNoteSchema = z
    .object({
    side: z.enum(['base', 'subject']),
    contract_clause_id: ContractClauseIdSchema,
    deviation_note: z.string().min(1).max(2000),
    source: z.enum(BASE_DEVIATION_NOTE_SOURCES),
    /** The evidence row; `null` only when none exists, and the page says so. */
    audit_id: OpaqueIdSchema.nullable(),
})
    .strict();
/**
 * The value comparison of a paired line (R-112-5): value **spans** in each
 * contract's `full_text` coordinates, in the order the tuple was compared.
 * The strings are not carried (rule 13 read strictly, Q-112-5); the page
 * slices them.
 */
export const BaseDeviationValuesSchema = z
    .object({
    equal: z.boolean(),
    base_spans: z.array(SpanSchema).max(MAX_VALUE_SPANS),
    subject_spans: z.array(SpanSchema).max(MAX_VALUE_SPANS),
})
    .strict();
const PAIRED = new Set(BASE_DEVIATION_PAIRED_PRECEDENCE);
const ONE_SIDED = new Set(BASE_DEVIATION_ONE_SIDED_CATEGORIES);
/** One line of the report. */
export const BaseDeviationLineSchema = z
    .object({
    category: BaseDeviationCategorySchema,
    /** `fingerprint` is R-112-7's text-equality pairing of paragraphs outside the library. */
    matched_by: z.enum(['library', 'fingerprint']),
    /** The clause, by code (R-112-1). `null` on a fingerprint pair and on `added_bespoke`. */
    clause_code: ClauseCodeSchema.nullable(),
    /** The library versions each side's paragraphs are matched to, ascending. Shown on every line. */
    clause_versions: z.object({ base: z.array(VersionNoSchema), subject: z.array(VersionNoSchema) }).strict(),
    base_paragraphs: z.array(BaseDeviationParagraphSchema).max(MAX_PARAGRAPHS_PER_LINE),
    subject_paragraphs: z.array(BaseDeviationParagraphSchema).max(MAX_PARAGRAPHS_PER_LINE),
    /** Present exactly on a paired line. */
    values: BaseDeviationValuesSchema.nullable(),
    notes: z.array(BaseDeviationNoteSchema).max(MAX_PARAGRAPHS_PER_LINE * 2),
    /** Any paragraph on the line is `provisional` (R-112-6). */
    provisional: z.boolean(),
    /** R-112-4 — set only on the one-sided categories. */
    confirmed: z.boolean().nullable(),
    /** How many of the other side's entries could hide the counterpart. Set with `confirmed`. */
    other_side_unresolved: Count.nullable(),
})
    .strict()
    .superRefine((line, ctx) => {
    const issue = (path, message) => {
        ctx.addIssue({ code: 'custom', path, message });
    };
    const paired = PAIRED.has(line.category);
    if (line.category === 'missing' && (line.subject_paragraphs.length > 0 || line.base_paragraphs.length === 0)) {
        issue(['subject_paragraphs'], 'a missing line has base paragraphs and no subject paragraphs');
    }
    if ((line.category === 'added_library_clause' || line.category === 'added_bespoke') &&
        (line.base_paragraphs.length > 0 || line.subject_paragraphs.length === 0)) {
        issue(['base_paragraphs'], 'an added line has subject paragraphs and no base paragraphs');
    }
    if (paired && (line.base_paragraphs.length === 0 || line.subject_paragraphs.length === 0)) {
        issue(['category'], 'a paired line has paragraphs on both sides');
    }
    if (ONE_SIDED.has(line.category) !== (line.confirmed !== null)) {
        issue(['confirmed'], 'confirmed is set on the missing and added categories only');
    }
    if ((line.confirmed === null) !== (line.other_side_unresolved === null)) {
        issue(['other_side_unresolved'], 'other_side_unresolved is set together with confirmed');
    }
    if (line.confirmed === true && line.other_side_unresolved !== 0) {
        issue(['confirmed'], 'a line is confirmed only when the other side has nothing unresolved');
    }
    if (paired !== (line.values !== null)) {
        issue(['values'], 'values are present exactly on a paired line');
    }
    const codeless = line.matched_by === 'fingerprint' || line.category === 'added_bespoke';
    if (codeless !== (line.clause_code === null)) {
        issue(['clause_code'], 'clause_code is null exactly on a fingerprint pair and on added_bespoke');
    }
    if (line.matched_by === 'fingerprint') {
        if (line.category !== 'unchanged' && line.category !== 'different_values') {
            issue(['category'], 'a fingerprint pair is unchanged or different_values');
        }
        if (line.base_paragraphs.length !== 1 || line.subject_paragraphs.length !== 1) {
            issue(['matched_by'], 'a fingerprint pair is one paragraph on each side');
        }
    }
    if (line.values !== null) {
        if (line.category === 'different_values' && line.values.equal) {
            issue(['values', 'equal'], 'different_values means the value tuples differ');
        }
        if ((line.category === 'unchanged' || line.category === 'different_library_version') && !line.values.equal) {
            issue(['values', 'equal'], 'a line whose values differ is different_values or modified');
        }
    }
});
/** One not-compared entry: a paragraph row with its reason, or a stretch of uncovered text. */
export const BaseDeviationNotComparedSchema = z.union([
    z
        .object({
        reason: z.enum(PARAGRAPH_NOT_COMPARED_REASONS),
        paragraph: BaseDeviationParagraphSchema,
    })
        .strict(),
    z
        .object({
        reason: z.literal('not_segmented_text'),
        span: SpanSchema,
        /** The stretch's length in `full_text` code units — "the characters of text no paragraph covers". */
        char_count: z.number().int().min(1),
    })
        .strict(),
]);
/** Per side: how much was compared, and why the rest was not. */
export const BaseDeviationSideCountsSchema = z
    .object({
    paragraphs: Count,
    compared: Count,
    not_compared: Count,
    /** Every {@link NOT_COMPARED_REASONS} key, zeros included — `not_segmented_text` counts stretches. */
    not_compared_by_reason: z.record(NotComparedReasonSchema, Count),
    uncovered_chars: Count,
})
    .strict()
    .superRefine((side, ctx) => {
    if (side.compared + side.not_compared !== side.paragraphs) {
        ctx.addIssue({ code: 'custom', path: ['compared'], message: 'compared + not_compared = paragraphs' });
    }
    const byParagraph = Object.entries(side.not_compared_by_reason)
        .filter(([reason]) => reason !== 'not_segmented_text')
        .reduce((sum, [, n]) => sum + n, 0);
    if (byParagraph !== side.not_compared) {
        ctx.addIssue({ code: 'custom', path: ['not_compared_by_reason'], message: 'the paragraph reasons sum to not_compared' });
    }
});
/**
 * Counts only — no spans, codes or notes. This is what the audited run row
 * stores (`clm.base_deviation_report.summary`, R-112-9), so a reader of the
 * subject's audit trail learns the report's shape and none of the base's terms.
 */
export const BaseDeviationSummarySchema = z
    .object({
    lines_by_category: z.record(BaseDeviationCategorySchema, Count),
    provisional_lines: Count,
    /** One-sided lines whose `confirmed` is false. */
    unconfirmed_lines: Count,
    /** Paired lines whose two sides are matched to different versions of the clause (R-112-14). */
    version_skewed_lines: Count,
    base: BaseDeviationSideCountsSchema,
    subject: BaseDeviationSideCountsSchema,
})
    .strict();
/** R-112-14 — what else the reader must know about one side. */
export const BaseDeviationSideCaveatsSchema = z
    .object({
    conversion_caveat: ConversionCaveatSchema.nullable(),
    processing_restricted: z.boolean(),
    /** Amendments of this agreement — their changes are not part of the comparison. */
    amendments_not_included: Count,
    /**
     * Rows whose span could not be judged by R-112-17 (no dated evidence of
     * when they were written). Compared, and counted here rather than silently
     * trusted.
     */
    spans_unverified: Count,
})
    .strict();
/** The immutable snapshot a run stores (`clm.base_deviation_report_result.result`). */
export const BaseDeviationResultSchema = z
    .object({
    result_schema_version: z.literal(BASE_DEVIATION_RESULT_SCHEMA_VERSION),
    lines: z.array(BaseDeviationLineSchema).max(MAX_LINES),
    not_compared: z
        .object({
        base: z.array(BaseDeviationNotComparedSchema).max(MAX_NOT_COMPARED),
        subject: z.array(BaseDeviationNotComparedSchema).max(MAX_NOT_COMPARED),
    })
        .strict(),
    summary: BaseDeviationSummarySchema,
    caveats: z.object({ base: BaseDeviationSideCaveatsSchema, subject: BaseDeviationSideCaveatsSchema }).strict(),
})
    .strict()
    .superRefine((result, ctx) => {
    for (const category of BASE_DEVIATION_CATEGORIES) {
        const tally = result.lines.filter((line) => line.category === category).length;
        if (result.summary.lines_by_category[category] !== tally) {
            ctx.addIssue({ code: 'custom', path: ['summary', 'lines_by_category', category], message: 'the summary counts the lines' });
        }
    }
    for (const side of ['base', 'subject']) {
        const paragraphs = result.not_compared[side].filter((entry) => entry.reason !== 'not_segmented_text').length;
        if (result.summary[side].not_compared !== paragraphs) {
            ctx.addIssue({ code: 'custom', path: ['summary', side, 'not_compared'], message: 'the summary counts the not-compared paragraphs' });
        }
    }
});
/* -------------------------------------------------------------------------- */
/* A report, as the run answers it and the GET re-opens it                     */
/* -------------------------------------------------------------------------- */
/** The base version a report was computed against (DR-11) — pinned, never re-resolved. */
export const BaseDeviationBaseRefSchema = z
    .object({
    nomination_id: NominationIdSchema,
    version_no: z.number().int().min(1),
    contract_key: ContractKeySchema,
})
    .strict();
/** Whether either contract's text moved since the run (`converted_utc` changed or is NULL now). */
const TextChangedSchema = z.object({ base: z.boolean(), subject: z.boolean() }).strict();
/**
 * The live half of a re-opened report (R-112-8). Everything here is read at
 * GET time; nothing in `result` is.
 */
export const BaseDeviationNowSchema = z
    .object({
    nomination_is_current: z.boolean(),
    base_superseded_utc: IsoTimestampUtcSchema.nullable(),
    /** A text change means the stored spans are unsafe: the page renders no slices for that side. */
    text_changed: TextChangedSchema,
    /** The inputs digest over both sides' rows differs from the one the run stored. */
    matches_changed: z.boolean(),
    /** The current library title of every clause code the result names, read live. */
    clause_titles: z.array(z.object({ clause_code: ClauseCodeSchema, title: z.string().min(1).max(200) }).strict()),
})
    .strict()
    .superRefine((now, ctx) => {
    if (now.nomination_is_current !== (now.base_superseded_utc === null)) {
        ctx.addIssue({ code: 'custom', path: ['base_superseded_utc'], message: 'a nomination is current exactly when it is not superseded' });
    }
});
export const BaseDeviationReportSchema = z
    .object({
    report_id: BaseDeviationReportIdSchema,
    contract_type: BaseContractTypeSchema,
    subject_contract_key: ContractKeySchema,
    base: BaseDeviationBaseRefSchema,
    requested_by_upn: UpnSchema,
    computed_utc: IsoTimestampUtcSchema,
    /** Tamper evidence: SHA-256 of the result's canonical JSON, as the audited run row carries it (R-112-9). */
    result_sha256: Sha256Schema,
    result: BaseDeviationResultSchema,
    now: BaseDeviationNowSchema,
})
    .strict()
    .superRefine((report, ctx) => {
    if (report.subject_contract_key === report.base.contract_key) {
        ctx.addIssue({ code: 'custom', path: ['base', 'contract_key'], message: 'a report never compares a contract with itself' });
    }
});
/* -------------------------------------------------------------------------- */
/* list-base-deviation-reports                                                 */
/* -------------------------------------------------------------------------- */
export const ListBaseDeviationReportsRequestSchema = z.object({ contract_key: ContractKeySchema }).strict();
/** One past run, newest first: its base version, its counts, and whether it is stale now. */
export const BaseDeviationRunSummarySchema = z
    .object({
    report_id: BaseDeviationReportIdSchema,
    computed_utc: IsoTimestampUtcSchema,
    requested_by_upn: UpnSchema,
    base: BaseDeviationBaseRefSchema,
    nomination_is_current: z.boolean(),
    summary: BaseDeviationSummarySchema,
    stale: z.object({ text_changed: TextChangedSchema, matches_changed: z.boolean() }).strict(),
})
    .strict();
export const ListBaseDeviationReportsResponseSchema = z
    .object({
    /** `practices` means `current_base: null` is "none visible to you", not "none exists" (R-111-7, R-112-12). */
    scope_kind: z.enum(['all', 'practices']),
    subject: z
        .object({
        contract_key: ContractKeySchema,
        contract_type: z.string().min(1).max(32),
        refusal: BaseDeviationSubjectRefusalSchema.nullable(),
        /** `null` when no base is visible: readiness is read against a base, under both contracts' scope. */
        readiness: BaseDeviationSideReadinessSchema.nullable(),
        conversion_caveat: ConversionCaveatSchema.nullable(),
    })
        .strict(),
    current_base: z
        .object({ nomination: BaseContractNominationSchema, readiness: BaseDeviationSideReadinessSchema })
        .strict()
        .nullable(),
    runs: z.array(BaseDeviationRunSummarySchema).max(500),
})
    .strict()
    .superRefine((body, ctx) => {
    if ((body.current_base === null) !== (body.subject.readiness === null)) {
        ctx.addIssue({ code: 'custom', path: ['subject', 'readiness'], message: 'subject readiness is read exactly when a base is visible' });
    }
    body.runs.forEach((run, index) => {
        const newer = body.runs[index - 1];
        if (newer !== undefined && BigInt(newer.report_id) <= BigInt(run.report_id)) {
            ctx.addIssue({ code: 'custom', path: ['runs', index], message: 'runs are newest first' });
        }
    });
});
/* -------------------------------------------------------------------------- */
/* run-base-deviation-report                                                   */
/* -------------------------------------------------------------------------- */
/**
 * Run a report for `contract_key` (the path) against `nomination_id` (the
 * body) — current or superseded, as long as the caller can see it (R-112-11).
 * There is no `expected_*` check: the caller named a specific version, so a
 * replacement meanwhile is not a conflict.
 */
export const RunBaseDeviationReportRequestSchema = z
    .object({ contract_key: ContractKeySchema, nomination_id: NominationIdSchema })
    .strict();
/** The new report — exactly what a GET of it answers — and its one `0046` `Create` row. */
export const RunBaseDeviationReportResponseSchema = z
    .object({
    report: BaseDeviationReportSchema,
    audit_ids: z.array(OpaqueIdSchema).length(1),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* get-base-deviation-report                                                   */
/* -------------------------------------------------------------------------- */
export const GetBaseDeviationReportRequestSchema = z.object({ report_id: BaseDeviationReportIdSchema }).strict();
export const GetBaseDeviationReportResponseSchema = BaseDeviationReportSchema;
//# sourceMappingURL=base-deviation-reports.js.map